import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, getMl } from "@/lib/api";
import MultilingualField from "@/components/MultilingualField";
import FileUploader from "@/components/FileUploader";

const ICON_OPTIONS = ["ShoppingBag", "Ship", "Anchor", "ShieldCheck", "Truck", "FileCheck", "BadgeCheck", "Stethoscope", "Handshake", "MapPin", "Warehouse", "Megaphone", "Sparkles", "Globe2"];

const empty = () => ({
  name: { en: "", tr: "", id: "" },
  short_description: { en: "", tr: "", id: "" },
  description: { en: "", tr: "", id: "" },
  icon: "Sparkles", image_url: "",
  social_links: {},
  sort_order: 0, is_published: true,
});

const AdminServices = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/services?published_only=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    try {
      if (editing?.id) await api.put(`/services/${editing.id}`, data);
      else await api.post("/services", data);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/services/${id}`);
    load();
  };

  return (
    <div data-testid="admin-services">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-3xl text-[#0A1128]">Services</h1>
        <button onClick={() => setEditing(empty())} data-testid="add-service-btn" className="btn-gold"><Plus size={14} /> Add Service</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 p-5 rounded-sm" data-testid={`service-row-${s.id}`}>
            <div className="text-[10px] text-slate-400 uppercase tracking-[0.2em]">{s.icon}</div>
            <h3 className="font-display text-lg mt-2 mb-1">{getMl(s.name, "en")}</h3>
            <p className="text-xs text-slate-600 line-clamp-2">{getMl(s.short_description, "en")}</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(s)} data-testid={`edit-svc-${s.id}`} className="text-slate-400 hover:text-[#0A1128]"><Pencil size={14} /></button>
              <button onClick={() => onDelete(s.id)} data-testid={`del-svc-${s.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="service-form-modal">
          <div className="bg-white max-w-2xl w-full rounded-sm my-10">
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "Add"} Service</h2>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <MultilingualField label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="svc-name" required />
              <MultilingualField label="Short Description" value={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} testId="svc-short" multiline />
              <MultilingualField label="Full Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="svc-desc" multiline />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Icon</label>
                  <select value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} data-testid="svc-icon" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm">
                    {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Sort Order</label>
                  <input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: +e.target.value })} data-testid="svc-sort" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm" />
                </div>
              </div>
              <FileUploader label="Image (optional)" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testId="svc-image" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} data-testid="svc-pub" /> Published</label>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5">Cancel</button>
              <button onClick={() => onSave(editing)} data-testid="svc-save" className="btn-gold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
